/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// application
import ApplicationState from "applicationstate/ApplicationState.js";

// models
import Application from "models/application/Application.js";

// orchestration
import Firebase from "orchestration/firebase-library/Firebase.js";

// services
import GoogleMapsService from "orchestration/services/google-maps-service/GoogleMapsService.js";
import PersistenceService from "orchestration/services/persistence-service/PersistenceService.js";
import StorageService from "orchestration/services/storage-service/StorageService.js";
import TransactionService from "orchestration/services/transaction-service/TransactionService.js";

class ApplicationService extends PersistenceService {

  /**
  * @param {Application} application
  */
  static applyCustomization(application) {
    ApplicationState.setState({ application });
    GoogleMapsService.applyCustomization(application);
    window.document.title = application.getTitle();
    const colors = application.getState().colors;
    const root = document.querySelector("html");
    for (const key in colors) {
      if (colors[key]) {
        const computeKey = `--${key}`;
        // TODO set corresponding hover tones to match new colors
        if(root.style.getPropertyValue(computeKey) !== colors[key]) {
          root.style.setProperty(computeKey, colors[key]);
        }
      }
    }
  }

  /**
  * Overrides PersistenceService delete
  * Application persistenceObject cannot be deleted
  *
  * @async
  * @override
  * @returns {Promise}
  */
  static async delete() {
    return new Promise((resolve, reject) => reject("Application cannot be deleted."));
  }

  /**
  * @async
  * @override
  * @param {Object} applicationState
  * @param {Boolean} ignoreMedia
  * @returns {Application}
  */
  static async format(applicationState, ignoreMedia = false) {
    const application = new Application(applicationState);
    if(!ignoreMedia) {
      await Promise.all(Array.of(
        StorageService.getImageUrls(application.getBannerImage()),
        StorageService.getImageUrls(application.getBootImage()),
        StorageService.getImageUrls(application.getDownloadImage()),
        StorageService.getImageUrls(application.getIconImage()),
        ...application.getQrCodeImages().map((value) => {
          return StorageService.getImageUrls(value);
        }),
      ));
    }
    return application;
  }

  /**
  * @async
  * @override
  * @param {Boolean} ignoreMedia
  * @param {Boolean} applyCustomization
  * @returns {Application}
  */
  static async get(ignoreMedia = false, applyCustomization = true) {
    const { application: apiPath } = Firebase.getApiPaths();
    const application = await super.get(apiPath, String("default"), ignoreMedia);
    if(applyCustomization) {
      ApplicationService.applyCustomization(application);
    }
    return application;
  }

  /**
  * @async
  * @override
  * @param {Application} application
  * @param {String} transactionId
  * @param {Function} onProgress
  */
  static async preProcessTransaction(application, transactionId, onProgress) {
    
    // reduce new images to remove duplicate data
    // and upload images to transaction ingest
    const bannerImage = application.getBannerImage();
    const bootImage = application.getBootImage();
    const downloadImage = application.getDownloadImage();
    const iconImage = application.getIconImage();
    const qrcodeImages = application.getQrCodeImages();

    const applicationImages = [
      bannerImage,
      bootImage,
      downloadImage,
      iconImage,
    ];

    // application images
    bannerImage.setId("bannerImage");
    bootImage.setId("bootImage");
    downloadImage.setId("downloadImage");
    iconImage.setId("iconImage");
    await TransactionService.processImageIngest(
      transactionId,
      applicationImages,
      onProgress,
    );

    // qrcode images
    if(qrcodeImages.length > Number("0")) {
      qrcodeImages.forEach((value) => value.setId("qrcodeImage"));
      await TransactionService.processImageIngest(
        transactionId,
        qrcodeImages,
        onProgress,
      );
    }

    return application;
  }

  /**
  * @async
  * @override
  * @param {Application} application
  * @param {Boolean} applyCustomization
  * @param {Function} onProgress
  * @returns {String} "success"
  */
  static async save(application, applyCustomization = true, onProgress) {
    const { application: apiPath } = Firebase.getApiPaths();
    await super.save(apiPath, application, onProgress);
    if(applyCustomization) {
      ApplicationService.applyCustomization(application);
    }
    return "success";
  }
}
export default ApplicationService;
